import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

/*
 * ════════════════════════════════════════════════════════════════════════
 * ONE LANGUAGE IS DOWNLOADED, NOT BOTH.
 * ════════════════════════════════════════════════════════════════════════
 *
 * This file used to carry 74 static imports -- 37 namespaces in each of two
 * languages -- and hand them all to init() as `resources`. Every visitor
 * therefore downloaded roughly 465 kB of JSON to read the app in one
 * language, and the half they could not read was about 230 kB of it.
 *
 * `import.meta.glob` gives one lazy loader per catalogue file instead, and
 * the backend below hands i18next whichever it asks for. Two consequences
 * worth stating:
 *
 *   * the namespace list and the resources are now DERIVED FROM DISK. A new
 *     catalogue is picked up by existing, and cannot be forgotten in a
 *     `resources` map -- which was a real failure mode this file had, and
 *     the reason catalogues.test.js checks registration at all.
 *   * i18next owns the loading. changeLanguage() waits for the bundle by
 *     itself, so the five call sites that switch language did not change.
 *
 * ── WHY fallbackLng IS OFF ──────────────────────────────────────────────
 *
 * i18next preloads the fallback language so a missing key can fall through
 * to it -- which would download English for every Hebrew reader and undo the
 * whole saving. It is off, and what makes that safe is not optimism: the two
 * catalogues are asserted to hold IDENTICAL keys (catalogues.test.js, one
 * test per namespace), so there is no key for a fallback to catch. If that
 * test is ever weakened, this line has to come back.
 *
 * ── OFFLINE ─────────────────────────────────────────────────────────────
 *
 * Lazy is not "needs the network". Each language becomes its own chunk (see
 * manualChunks in vite.config.js), every emitted chunk is listed in
 * asset-manifest.json, and the service worker precaches all of them at
 * install. So a reader who has loaded the app once can still switch language
 * with no signal. src/i18n/lazyCatalogues.test.js holds the derivation to
 * that promise.
 */
const CATALOGUES = import.meta.glob('./locales/*/*.json');

const key = (language, namespace) => `./locales/${language}/${namespace}.json`;

/** Every namespace present on disk, in a stable order. */
export const NAMESPACES = [...new Set(
  Object.keys(CATALOGUES).map(path => path.split('/').pop().replace('.json', '')),
)].sort();

/** Every language directory present on disk. */
export const LANGUAGES = [...new Set(
  Object.keys(CATALOGUES).map(path => path.split('/')[2]),
)].sort();

const STORAGE_KEY = 'mb_lang';
const RTL_LANGUAGES = ['he'];

function applyDirection(lng) {
  const dir = RTL_LANGUAGES.includes(lng) ? 'rtl' : 'ltr';
  document.documentElement.dir = dir;
  document.documentElement.lang = lng;
}

const savedLang = (() => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;   // private window, or site data blocked
  }
})();
const initialLang = savedLang === 'en' ? 'en' : 'he';

/**
 * An i18next backend over the glob. One catalogue per (language, namespace),
 * so a language nobody selects is never fetched and a namespace nobody opens
 * is not either.
 */
export const lazyCatalogues = {
  type: 'backend',
  init() {},
  read(language, namespace, callback) {
    const load = CATALOGUES[key(language, namespace)];
    if (!load) {
      // Not an error worth throwing: i18next asks for combinations that may
      // not exist, and a missing catalogue must degrade to the key rather
      // than take the screen down.
      callback(null, {});
      return;
    }
    load().then(
      module => callback(null, module.default || module),
      error => callback(error, false),
    );
  },
};

export const ready = i18n
  .use(lazyCatalogues)
  .use(initReactI18next)
  .init({
    lng: initialLang,
    // Off deliberately -- see the note above. The catalogues are asserted to
    // hold identical keys, so nothing needs catching, and preloading English
    // for a Hebrew reader is exactly the download this change removes.
    fallbackLng: false,
    supportedLngs: LANGUAGES,
    ns: NAMESPACES,
    defaultNS: 'common',
    // The app awaits `ready` before its first render (src/index.js), so the
    // initial language is in hand by then and nothing has to suspend. A
    // LATER changeLanguage is what Suspense is for, and index.js provides
    // the boundary.
    react: { useSuspense: true },
    interpolation: { escapeValue: false },
  });

applyDirection(initialLang);

i18n.on('languageChanged', (lng) => {
  try {
    localStorage.setItem(STORAGE_KEY, lng);
  } catch {
    // not persisted; the choice still applies for this session
  }
  applyDirection(lng);
});

export default i18n;
